import { memo, ReactNode, useEffect, useRef, useState } from 'react';

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import { DAAS_PROJECT_TYPE } from '../../../../types';
import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { DAAS_NAME_MAX_LENGTH } from '../../nameLimits';
import { PROJECT_DESCRIPTION_INFO, PROJECT_NAME_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import { ProjectBasicInfoValue } from '../types';
import { projectNameFormatErrors } from '../validation';

const DESCRIPTION_MAX_LENGTH = 250;

export interface BasicInfoSectionProps {
  value: ProjectBasicInfoValue;
  onChange: (value: ProjectBasicInfoValue) => void;
  errors?: string[];
}

const BasicInfoSectionInner = ({
  value,
  onChange,
  errors = [],
}: BasicInfoSectionProps) => {
  const { classes } = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [nameWarning, setNameWarning] = useState<string | undefined>();
  const projectNameRef = useRef<HTMLDivElement>(null);
  const existingNamesRef = useRef<Set<string> | null>(null);

  const projectNameError =
    errors.length > 0 ? projectNameFormatErrors(value.projectName) : '';
  const hasError = projectNameError !== '';

  useEffect(() => {
    if (hasError || nameWarning) {
      projectNameRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [hasError, nameWarning]);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'Component', 'spec.type': DAAS_PROJECT_TYPE },
      })
      .then(result => {
        if (cancelled) return;
        existingNamesRef.current = new Set(
          result.items.map(e => e.metadata.name),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  useEffect(() => {
    if (!value.projectName || !value.projectName.trim()) {
      setNameWarning(undefined);
      return;
    }
    if (existingNamesRef.current === null) return;
    setNameWarning(
      existingNamesRef.current.has(value.projectName)
        ? `Project "${value.projectName}" already exists in the catalog`
        : undefined,
    );
  }, [value.projectName]);

  let projectNameHelperText: ReactNode;
  if (nameWarning) {
    projectNameHelperText = <ScaffolderFieldError message={nameWarning} />;
  } else if (hasError) {
    projectNameHelperText = <ScaffolderFieldError message={projectNameError} />;
  } else {
    projectNameHelperText =
      'Up to 46 characters. Allowed: lowercase letters, numbers, hyphens.';
  }

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Project configuration"
          description="Configure a new Deployment as a Service Project"
          variant="h5"
          divider
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Project Name"
            description="Specify a unique name for the project."
            infoDoc={PROJECT_NAME_INFO}
          />
          <TextField
            ref={projectNameRef}
            label="Project Name"
            value={value.projectName}
            onChange={e => onChange({ ...value, projectName: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            required
            error={hasError || Boolean(nameWarning)}
            slotProps={{
              htmlInput: { maxLength: DAAS_NAME_MAX_LENGTH },
            }}
            helperText={projectNameHelperText}
          />
        </Box>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Description"
            description="Specify a description of the project."
            infoDoc={PROJECT_DESCRIPTION_INFO}
          />
          <TextField
            label="Description"
            value={value.description}
            onChange={e => onChange({ ...value, description: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={5}
            slotProps={{
              htmlInput: { maxLength: DESCRIPTION_MAX_LENGTH },
            }}
            helperText="Optional. Up to 250 characters."
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const BasicInfoSection = memo(BasicInfoSectionInner);
